import MainAccordion from "components/MainAccordion/MainAccordion.view";
import { MainTable } from "components/MainTable";
import useRCASettingsColumns from "../../useRCASettingsColumns";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { useTranslation } from "react-i18next";
import { useRCAMainStyles } from "../../../RCA.styles";
import { Button } from "@mui/material";
import { useStyles } from "../../RCASettings.styles";
import { IRCASettingsReturn } from "entities/RCA/RCASettingsEntity/RCASettingsEntityInterfaces";
import { ApolloError, ApolloQueryResult, OperationVariables } from "@apollo/client";
import { useCallback, useContext, useMemo } from "react";
import { RCASettingsContext } from "contexts/RCASettingsContext.ts/RCASettingsContext";
import { RCASettingsEntity } from "entities/RCA/RCASettingsEntity/RCASettingsEntity";

const RCASettingsAccordion = ({
    rcaSetting,
    apolloQueryError,
    loading,
    defaultExpanded,
    canEdit,
    refetch,
}: {
    rcaSetting: IRCASettingsReturn;
    apolloQueryError: ApolloError | undefined;
    loading: boolean;
    defaultExpanded: boolean;
    canEdit: boolean | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}) => {
    const mainClasses = useRCAMainStyles();
    const { t } = useTranslation();
    const classes = useStyles();
    const { rcaSettingsChanges, setRcaSettingsChanges } = useContext(RCASettingsContext);
    const { upsertRCASettingsData, error } = RCASettingsEntity.UpsertRCASettings();
    const tableColumns = useRCASettingsColumns(canEdit) as unknown as IMainTableColumn[];

    const unsavedChanges = useMemo(() => {
        return rcaSettingsChanges.filter((rcaSettingChanges) => {
            const originalRCASetting = rcaSetting.rca_settings.find(
                (originalRCASettingValue) =>
                    originalRCASettingValue.failure_type.failure_type_id ===
                    rcaSettingChanges.failure_type.failure_type_id,
            );

            return (
                rcaSettingChanges.production_line_id === rcaSetting.production_line_id &&
                ((rcaSettingChanges?.owner_id ? rcaSettingChanges?.owner_id !== originalRCASetting?.owner_id : false) ||
                    (rcaSettingChanges?.rca_duration_trigger?.value
                        ? rcaSettingChanges?.rca_duration_trigger?.value !==
                          originalRCASetting?.rca_duration_trigger?.value
                        : false) ||
                    rcaSettingChanges?.rca_duration_trigger?.is_enabled !==
                        originalRCASetting?.rca_duration_trigger?.is_enabled ||
                    (rcaSettingChanges?.rca_frequency_trigger?.rca_frequency_trigger_unit_id
                        ? rcaSettingChanges?.rca_frequency_trigger?.rca_frequency_trigger_unit_id !==
                          originalRCASetting?.rca_frequency_trigger?.rca_frequency_trigger_unit_id
                        : false) ||
                    (rcaSettingChanges?.rca_frequency_trigger?.value
                        ? rcaSettingChanges?.rca_frequency_trigger?.value !==
                          originalRCASetting?.rca_frequency_trigger?.value
                        : false) ||
                    rcaSettingChanges?.rca_frequency_trigger?.is_enabled !==
                        originalRCASetting?.rca_frequency_trigger?.is_enabled)
            );
        });
    }, [rcaSettingsChanges, rcaSetting]);

    const disableSaving = useMemo(
        () => unsavedChanges.find((unsavedChange) => !unsavedChange.owner_id),
        [unsavedChanges],
    );

    const handleSaveRCASettings = useCallback(async () => {
        const rcaSettingsUpsertData = unsavedChanges.map((changes) => ({
            ...(changes.rca_settings_id ? { rca_settings_id: changes.rca_settings_id } : {}),
            failure_type_id: changes.failure_type.failure_type_id,
            owner_id: changes.owner_id,
            production_line_id: changes.production_line_id,
            rca_duration_trigger: {
                data: {
                    ...(changes?.rca_duration_trigger?.rca_duration_trigger_id
                        ? { rca_duration_trigger_id: changes?.rca_duration_trigger?.rca_duration_trigger_id }
                        : {}),
                    is_enabled: changes.rca_duration_trigger.is_enabled,
                    value: changes.rca_duration_trigger.value,
                },
                on_conflict: {
                    constraint: "rca_duration_trigger_pkey",
                    update_columns: ["is_enabled", "value"],
                },
            },
            ...(changes.rca_frequency_trigger.rca_frequency_trigger_unit_id
                ? {
                      rca_frequency_trigger: {
                          data: {
                              ...(changes?.rca_frequency_trigger?.rca_frequency_trigger_id
                                  ? {
                                        rca_frequency_trigger_id:
                                            changes?.rca_frequency_trigger?.rca_frequency_trigger_id,
                                    }
                                  : {}),
                              is_enabled: changes.rca_frequency_trigger.is_enabled,
                              value: changes.rca_frequency_trigger.value,
                              rca_frequency_trigger_unit_id:
                                  changes.rca_frequency_trigger.rca_frequency_trigger_unit_id,
                          },
                          on_conflict: {
                              constraint: "rca_frequency_trigger_pkey",
                              update_columns: ["is_enabled", "value", "rca_frequency_trigger_unit_id"],
                          },
                      },
                  }
                : {}),
        }));

        await upsertRCASettingsData({ variables: { rcaSettings: rcaSettingsUpsertData } });
        if (!error) {
            setRcaSettingsChanges((prev) =>
                prev.filter(
                    (prevValue) => prevValue.production_line_id !== rcaSettingsUpsertData[0].production_line_id,
                ),
            );
            await refetch();
        }
    }, [unsavedChanges, refetch, upsertRCASettingsData, error, setRcaSettingsChanges]);

    return (
        <MainAccordion
            title={rcaSetting.name}
            fullWidth
            key={rcaSetting.production_line_id}
            defaultExpanded={defaultExpanded}
            defaultIsExpandedState={defaultExpanded}
            unsavedChanges={Boolean(unsavedChanges.length)}
        >
            <MainTable
                maxHeight={false}
                customStyle={mainClasses.contentTable}
                paperElevation={0}
                customStyleTypography={{ width: "90%" }}
                paperCustomStyle={mainClasses.contentTablePaperMyDashboard}
                columns={tableColumns}
                tableCellCustomStyle={mainClasses.contentTableCell}
                rows={!loading && !apolloQueryError ? rcaSetting.rca_settings : []}
                loading={loading}
            />
            <div className={classes.footerCardActions}>
                <Button
                    variant="contained"
                    color="info"
                    className={mainClasses.contentButton}
                    onClick={handleSaveRCASettings}
                    disabled={!canEdit || !Boolean(unsavedChanges.length) || Boolean(disableSaving)}
                >
                    {t("rca.settings.save")}
                </Button>
            </div>
        </MainAccordion>
    );
};

export default RCASettingsAccordion;
